/** Third party imports */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnDestroy,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, finalize, forkJoin, map, of, type Observable } from 'rxjs';

/** Component imports */
import { Button, Card, Input, Skeleton, Tabs } from '@ntv360/component-pantry';
/** Local imports */
import { DatePipe, UpperCasePipe } from '@angular/common';
import { ApiUser, DealerService, UsersService } from '@core';
import { UpdateUserDto } from '@core/dto';
import { ApiDealerInformation } from '@core/services/dealer/dealer.interface';
import { LicensesService } from '@core/services/licenses/licenses.service';
import { SINGLE_DEALER_LICENSE_TABLE_COLUMNS } from '@features/dealers/constants/single-dealer-license-table.columns';
import { UILicense } from '@features/licenses/interfaces/ui-license-table.interface';
import { INPUT_INFORMATION_STEP_LAYOUT } from '@features/users/constants';
import { UiDealersAutocomplete, UserRoles } from '@features/users/interfaces';
import {
    extractCreatedByUserId,
    formatCreatedByResolvedName,
    getCreatedByDisplayLabel,
} from '@features/users/users.utils';
import { ConfirmationModalComponent, FilterOperator } from '@shared';
import { SVG_ICONS } from '@shared/constants';
import { TableToolbarButton } from '@shared/interfaces';
import { comparePasswords, formBuilder, Logger, searchHandler, withLoading } from '@shared/utils';
import { MainLayoutStore } from '../../../../layout/main-layout/main-layout.store';
import { SINGLE_USER_ICONS_SPRITE_PATH, SINGLE_USER_TABS } from './single-user.constants';
import type {
    AggregatedSingleUserRequests,
    DealerAssignmentRow,
    LoadSingleUserResponse,
    SingleUserNavigationState,
} from './single-user.interfaces';

@Component({
    selector: 'app-single-user',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        Button,
        Card,
        Input,
        Tabs,
        ReactiveFormsModule,
        DatePipe,
        UpperCasePipe,
        Skeleton,
        ConfirmationModalComponent,
    ],
    templateUrl: './single-user.component.html',
    styleUrl: './single-user.component.scss',
})
export class SingleUserComponent implements OnDestroy {
    /** Error message shown when contact number input exceeds digit limit */
    private static readonly CONTACT_NUMBER_LIMIT_ERROR = 'Contact Number must be 10 digits only';

    /** Activated route for reading the `id` param */
    private readonly route = inject(ActivatedRoute);

    /** Router for post-save navigation back to users list */
    private readonly router = inject(Router);

    /** Reactive form builder */
    private readonly fb = inject(FormBuilder);

    /** Tears down subscriptions when the component is destroyed */
    private readonly destroyRef = inject(DestroyRef);

    /** Users API */
    private readonly userService = inject(UsersService);

    /** Dealers API */
    private readonly dealerService = inject(DealerService);

    /** Licenses API (rows owned by the viewed user) */
    private readonly licensesService = inject(LicensesService);

    /** User id from the route */
    private readonly userId = toSignal(this.route.paramMap.pipe(map((params) => params.get('id'))));

    /** SVG sprite symbol keys for the template */
    public readonly icons = SVG_ICONS;

    /** Sprite asset URL */
    public readonly spritePath = SINGLE_USER_ICONS_SPRITE_PATH;

    /** Tabs rendered above the user form */
    public tabs = SINGLE_USER_TABS;

    /** Roles that may manage associated dealers in the (future) extended form */
    protected readonly allowedRoles: readonly string[] = ['superAdmin', 'dealerAdmin'];

    /** Loaded user row from the API or navigation state */
    protected readonly userInfo = signal<ApiUser | null>(null);

    /** True while the primary user request is in flight */
    protected readonly isLoadingUser = signal<boolean>(false);

    /** Dealer options for autocomplete (dealer-admin flows) */
    protected readonly organizationDealers = signal<UiDealersAutocomplete[]>([]);

    /** Debounced dealer search text */
    protected readonly dealerSearchKey = signal<string>('');

    /** True while dealer typeahead is loading */
    protected readonly isSearchingDealer = signal<boolean>(false);

    /** True while PATCH user is in flight */
    protected readonly isUpdatingUser = signal<boolean>(false);

    /** Tracks whether user attempted to exceed contact number limit */
    protected readonly isContactNumberLimitExceeded = signal<boolean>(false);

    /** Active tab index for `ntv-tabs` */
    public readonly activeTab = signal(0);

    /** Selected dealers for dealer-admin association UI */
    public readonly selectedDealers = signal<{ label: string; value: string }[]>([]);

    /** Snapshot of dealers returned from the server for cancel/diff */
    protected readonly originalSelectedDealers = signal<{ label: string; value: string }[]>([]);

    /** Drives the post-update confirmation modal */
    protected readonly isUserUpdated = signal<boolean>(false);

    /** Licenses table: column config (shared shape with single-dealer view) */
    public readonly licenseColumns = SINGLE_DEALER_LICENSE_TABLE_COLUMNS;

    private readonly licenseSearch = signal('');

    public readonly licenseRows = signal<UILicense[]>([]);

    public readonly licensesLoading = signal(false);

    public readonly toolbarButtons = signal<TableToolbarButton[]>([]);

    public readonly filteredLicenseRows = computed(() => {
        const q = this.licenseSearch().trim().toLowerCase();
        const rows = this.licenseRows();
        if (!q) {
            return rows;
        }
        return rows.filter((row) => {
            const id = (row.id ?? '').toLowerCase();
            const host = (row.host ?? '').toLowerCase();
            const alias = (row.alias ?? '').toLowerCase();
            const dealer = (row.dealer ?? '').toLowerCase();
            const status = (row.displayStatus ?? '').toLowerCase();
            const anydesk = (row.anydesk ?? [])
                .map((a) => `${a.label}${a.value ?? ''}`)
                .join(' ')
                .toLowerCase();
            const key = (row.licenseKey ?? '').toLowerCase();
            return (
                id.includes(q) ||
                key.includes(q) ||
                host.includes(q) ||
                alias.includes(q) ||
                dealer.includes(q) ||
                status.includes(q) ||
                anydesk.includes(q)
            );
        });
    });

    /** View vs edit mode for basic user fields */
    protected readonly isEditingUser = signal(false);

    /** Whether the extended template should show dealer management */
    protected readonly showDealerManagement = computed(() => {
        return this.allowedRoles.includes(this.userRole());
    });

    /** Internal role key driving `INPUT_INFORMATION_STEP_LAYOUT` */
    protected readonly userRole = signal<Exclude<UserRoles, 'none'>>('dealerAdmin');

    /** Badge text for the viewed user's client type */
    protected readonly clientType = signal<string>('User');

    /** Resolved creator label for the subtitle (same rules as users table "Created By") */
    protected readonly createdByDisplayName = signal<string>('N/A');

    /** Directus-style filter for dealers owned by the viewed user */
    private readonly userDealersFilter = computed(() => {
        const userId = this.userId();
        if (!userId) {
            return {};
        }
        return {
            'clientInformation.ownerId': {
                _eq: userId,
            },
        };
    });

    /**
     * Reactive form for the user row.
     *
     * @remarks Exposed for reuse from parent flows such as create-user.
     */
    public readonly form = computed(() => {
        return formBuilder(this.fb, INPUT_INFORMATION_STEP_LAYOUT[this.userRole()] ?? [], [
            this.comparePasswords,
        ]);
    });

    /** Non-password fields for the current role layout */
    protected readonly userInfoFields = computed(() => {
        return INPUT_INFORMATION_STEP_LAYOUT[this.userRole()]
            ?.filter((fld) => fld.name !== 'password' && fld.name !== 'passwordConfirmation')
            .map((fld) => ({
                placeholder: fld.placeholder,
                fieldName: fld.name,
                label: fld.label,
                type: fld.type,
                gridSpan: fld.gridSpan,
            }));
    });

    /** Password field descriptors when that section is enabled */
    protected readonly userPasswordFields = computed(() => {
        return INPUT_INFORMATION_STEP_LAYOUT[this.userRole()]
            ?.filter((fld) => fld.name === 'password' || fld.name === 'passwordConfirmation')
            .map((fld) => ({
                placeholder: fld.placeholder,
                fieldName: fld.name,
                label:
                    fld.name === 'password'
                        ? 'New Password'
                        : fld.name === 'passwordConfirmation'
                          ? 'Re-type New Password'
                          : fld.label,
                type: fld.type,
                gridSpan: fld.gridSpan,
            }));
    });

    /**
     * @public
     * @description Wires dealer search, route-driven user load, license loading, breadcrumb updates, and form lifecycle side effects.
     * @returns void
     */
    public constructor() {
        searchHandler(
            this.dealerSearchKey,
            (searchKey) => {
                return this.dealerService
                    .search(searchKey)
                    .pipe(withLoading(this.isSearchingDealer));
            },
            this.destroyRef,
            {
                next: (data) => {
                    this.organizationDealers.set(
                        data?.map(
                            (d) =>
                                ({
                                    ...d,
                                    value: d.dealerId,
                                    label: d.dealerName,
                                }) as UiDealersAutocomplete,
                        ) ?? [],
                    );
                },
                onEmpty: () => {
                    this.loadUserDealers();
                },
                error: () => undefined,
            },
        );

        const state = history.state as SingleUserNavigationState;
        if (state?.userData) {
            const userData = state.userData;
            // Split name into first and last if needed
            const nameParts = (userData.name || '').split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            // Set initial user info for display
            this.userInfo.set({
                ...userData,
                firstName,
                lastName,
            } as ApiUser);

            // Set role and client type dynamically from state
            const initialRole = String(userData.clientType ?? userData.role ?? 'User');
            this.userRole.set(this.mapRoleToInternalRole(initialRole));
            this.clientType.set(this.formatRoleName(initialRole));
        }

        // Patch form immediately
        this.patchUserForm();

        const initialUser = this.userInfo();
        if (initialUser) {
            this.createdByDisplayName.set(getCreatedByDisplayLabel(initialUser));
            this.resolveCreatedByDisplayName(initialUser);
        }

        // Effect to update breadcrumb
        effect(() => {
            const user = this.userInfo();
            if (user) {
                MainLayoutStore.setBreadcrumb(`${user.firstName} ${user.lastName}`);
            }
        });

        // Effect to load user
        effect(() => {
            const userId = this.userId();
            const filter = untracked(() => this.userDealersFilter());

            untracked(() => {
                if (userId) {
                    this.loadUser(userId, filter as Record<string, FilterOperator>);
                }
            });
        });

        this.handlePasswordValidationState();
        this.handleContactNumberValidationState();

        effect(() => {
            const editing = this.isEditingUser();
            const formGroup = this.form();
            const fields = this.userInfoFields();
            untracked(() => {
                fields.forEach((field) => {
                    const control = formGroup.get(field.fieldName);
                    if (!control) return;
                    if (editing) {
                        control.enable({ emitEvent: false });
                    } else {
                        control.disable({ emitEvent: false });
                    }
                });
            });
        });
    }

    /**
     * @public
     * @description Clears the breadcrumb title when leaving the screen
     * @returns void
     */
    public ngOnDestroy(): void {
        MainLayoutStore.clearBreadcrumb();
    }

    /**
     * @public
     * @description Updates the active tab index when the user switches tabs
     * @param index - New tab index
     * @returns void
     */
    public onTabChange(index: number): void {
        this.activeTab.set(index);
    }

    /**
     * @protected
     * @description Updates the licenses table client-side search query (filters `filteredLicenseRows`).
     * @param value - Raw search string from the toolbar
     * @returns void
     */
    protected onLicenseSearchChange(value: string): void {
        this.licenseSearch.set(value);
    }

    /**
     * @protected
     * @description Placeholder for license row toolbar actions; reserved for future wiring.
     * @param _event - Action id and row payload from the table panel (unused)
     * @returns void
     */
    protected onLicenseAction(_event: { action: string; row: UILicense }): void {
        void _event;
    }

    /**
     * @protected
     * @description Enables editing for basic user info fields and shows Save and Cancel in the header
     * @returns void
     */
    protected onEditUserClick(): void {
        this.isEditingUser.set(true);
    }

    /**
     * @protected
     * @description Reverts the form and dealer chips to the last loaded server state and exits edit mode
     * @returns void
     */
    protected cancelEdit(): void {
        this.patchUserForm();
        this.selectedDealers.set(
            this.originalSelectedDealers().map((d) => ({ label: d.label, value: d.value })),
        );
        this.isEditingUser.set(false);
        const fg = this.form() as FormGroup;
        Object.keys(fg.controls).forEach((key) => {
            fg.get(key)?.markAsUntouched({ onlySelf: true });
        });
    }

    /**
     * @protected
     * @description Reads a scalar field from `userInfo` for display helpers
     * @param fieldName - Key on the user model
     * @returns Coerced string value or an empty string
     */
    protected getFieldValue(fieldName: string): string {
        const userInfo = this.userInfo();

        return (userInfo?.[fieldName as keyof typeof userInfo] ?? '') as string;
    }

    /**
     * @protected
     * @description Maps reactive validation errors to user-facing copy from the layout config
     * @param fieldName - Form control name
     * @returns List of messages for the control (empty when untouched or valid)
     */
    protected getFieldErrorMessage(fieldName: string): string[] {
        if (this.isContactNumberField(fieldName) && this.isContactNumberLimitExceeded()) {
            return [SingleUserComponent.CONTACT_NUMBER_LIMIT_ERROR];
        }

        const control = this.form().get(fieldName);
        if (!control || !control.touched) return [];

        const field = INPUT_INFORMATION_STEP_LAYOUT[this.userRole()]?.find(
            (f) => f.name === fieldName,
        );

        if (!field?.errors) return [];

        return Object.keys(control.errors ?? {})
            .map((errorKey) => field.errors![errorKey])
            .filter(Boolean);
    }

    /**
     * @protected
     * @description Marks a control touched so mismatch messaging can appear (password flows)
     * @param fieldName - Form control name
     * @returns void
     */
    protected onFocus(fieldName: string): void {
        const confirmPasswordControl = this.form().get(fieldName);

        if (!confirmPasswordControl) return;

        confirmPasswordControl.markAsTouched();
    }

    /**
     * @protected
     * @description Prevents non-numeric input and caps contact number to 10 digits
     * @param event - Keyboard event from the contact number field
     * @param fieldName - Target form control name
     * @returns void
     */
    protected onNumericKeydown(event: KeyboardEvent, fieldName: string): void {
        if (!this.isContactNumberField(fieldName)) return;

        const allowedControlKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'Home',
            'End',
        ];

        if (allowedControlKeys.includes(event.key)) return;
        if (event.ctrlKey || event.metaKey) return;
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
            return;
        }

        const currentValue = String(this.form().get(fieldName)?.value ?? '');
        if (currentValue.replace(/\D/g, '').length >= 10) {
            this.isContactNumberLimitExceeded.set(true);
            event.preventDefault();
        }
    }

    /**
     * @protected
     * @description Clears contact number overflow message when user leaves the field
     * @param fieldName - Target form control name
     * @returns void
     */
    protected onNumericBlur(fieldName: string): void {
        if (!this.isContactNumberField(fieldName)) return;
        this.isContactNumberLimitExceeded.set(false);
    }

    /**
     * @protected
     * @description Returns true when the field is the contact number input
     * @param fieldName - Candidate control name
     * @returns True when field is contact number
     */
    protected isContactNumberField(fieldName: string): boolean {
        return fieldName === 'contactNumber';
    }

    /**
     * @protected
     * @description Determines whether Save Changes should be enabled while editing
     * @returns True when form is valid and no update call is in flight
     */
    protected canSaveChanges(): boolean {
        return this.isEditingUser() && !this.isUpdatingUser() && this.form().valid;
    }

    /**
     * @protected
     * @description Closes the update success modal and returns to users list
     * @returns void
     */
    protected closeUpdatedModal(): void {
        this.isUserUpdated.set(false);
        void this.router.navigate(['/users']).then((navigated) => {
            if (navigated) {
                window.location.reload();
            }
        });
    }

    /**
     * @protected
     * @description Patches the reactive form from `userInfo`
     * @returns void
     */
    protected patchUserForm(): void {
        const userInfo = this.userInfo();

        if (!userInfo) return;

        this.form().patchValue({
            ...userInfo,
        });
    }

    /**
     * @protected
     * @description Persists the debounced dealer search term for autocomplete
     * @param searchKey - Raw search text from the UI
     * @returns void
     */
    protected onDealerSearch(searchKey: string): void {
        this.dealerSearchKey.set(searchKey);
    }

    /**
     * @protected
     * @description Placeholder for dealer multi-select change handling when that UI is enabled
     * @param dealers - Selected dealer rows from autocomplete
     * @returns void
     */
    protected onDealerSelect(dealers: Partial<UiDealersAutocomplete>[]): void {
        void dealers;
    }

    /**
     * @protected
     * @description Removes a dealer chip from the pending selection list
     * @param dealerValue - Stable dealer identifier (alias or id)
     * @returns void
     */
    protected removeDealer(dealerValue: string): void {
        this.selectedDealers.update((dealers) => dealers.filter((d) => d.value !== dealerValue));
    }

    /**
     * @protected
     * @description Validates dirty fields, builds a PATCH payload, and calls the update API when in edit mode
     * @returns void
     */
    protected saveInformation(): void {
        if (!this.isEditingUser()) return;

        const originalUserInfo = this.userInfo() as UpdateUserDto;
        const updatedInfo = this.form().getRawValue() as UpdateUserDto;
        if (!updatedInfo || !originalUserInfo) return;

        // Check if password fields are empty
        const { hasPassword, hasConfirmPassword } = this.checkPasswordFields();

        const updatedFields = this.buildPatch(originalUserInfo, updatedInfo);

        // Remove password fields if they are empty
        if (!hasPassword && !hasConfirmPassword) {
            delete updatedFields.password;
            delete updatedFields.passwordConfirmation;
        }

        // If fields are updated, validate them
        if (Object.keys(updatedFields).length > 0) {
            const isValid = this.validateForm();
            if (!isValid) return;
        }

        // If both are filled, validate them before proceeding
        if (hasPassword && hasConfirmPassword) {
            const form = this.form() as FormGroup;
            const password = form.get('password');
            const confirmPassword = form.get('passwordConfirmation');

            const bothValid = password?.valid && confirmPassword?.valid;
            if (!bothValid) {
                password?.markAsTouched();
                confirmPassword?.markAsTouched();
                return;
            }
        }

        // If only one of the two fields has value, ask the user to fill out the other one
        if (hasPassword !== hasConfirmPassword) {
            this.makeOtherPasswordFieldRequired();
            return;
        }

        // Check for dealer changes
        const originalDealersJoined = this.originalSelectedDealers()
            .map((d) => d.value)
            .sort()
            .join(',');
        const currentDealersJoined = this.selectedDealers()
            .map((d) => d.value)
            .sort()
            .join(',');

        if (originalDealersJoined !== currentDealersJoined) {
            updatedFields.dealers = this.selectedDealers().map((d) => d.value);
        }

        // Update user info
        const userId = this.userInfo()?.userId;
        if (!userId || Object.keys(updatedFields).length === 0) return;

        this.updateUser(userId, updatedFields);
    }

    /**
     * @private
     * @description Subscribes to password value changes so empty passwords stay optional
     * @returns void
     */
    private handlePasswordValidationState(): void {
        const form = this.form() as FormGroup;
        const password = form.get('password');
        const confirmPassword = form.get('passwordConfirmation');

        if (!password || !confirmPassword) return;

        form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            const hasPassword = !!password.value;
            const hasConfirmPassword = !!confirmPassword.value;

            if (!hasPassword && !hasConfirmPassword) {
                password.clearValidators();
                confirmPassword.clearValidators();
                password.setErrors(null);
                confirmPassword.setErrors(null);
            } else {
                password.setValidators([Validators.required, Validators.minLength(8)]);
                confirmPassword.setValidators([Validators.required]);
            }

            password.updateValueAndValidity({ emitEvent: false });
            confirmPassword.updateValueAndValidity({ emitEvent: false });
        });
    }

    /**
     * @private
     * @description Sanitizes contact number while typing/pasting to keep only 10 digits
     * @returns void
     */
    private handleContactNumberValidationState(): void {
        const contactNumberControl = this.form().get('contactNumber');
        if (!contactNumberControl) return;

        contactNumberControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                const rawValue = String(value ?? '');
                const sanitizedValue = rawValue.replace(/\D/g, '').slice(0, 10);
                const attemptedOverflow = rawValue.replace(/\D/g, '').length > 10;

                if (attemptedOverflow) {
                    this.isContactNumberLimitExceeded.set(true);
                } else if (sanitizedValue.length < 10) {
                    this.isContactNumberLimitExceeded.set(false);
                }

                if (rawValue !== sanitizedValue) {
                    contactNumberControl.setValue(sanitizedValue);
                }
            });
    }

    /**
     * @private
     * @description Cross-field validator delegating to the shared password comparison helper
     * @param control - Password group control
     * @returns Validation errors or null when valid
     */
    private comparePasswords(control: AbstractControl): ValidationErrors | null {
        return comparePasswords(control);
    }

    /**
     * @private
     * @description Copies API user and dealer rows into component signals and repatches the form
     * @param apiUserData - User entity returned from `getUserById`
     * @param apiDealersData - Dealer directory rows for admin contexts
     * @param currentDealers - Dealers linked to the viewed user
     * @returns void
     */
    private mapDataToUI(
        apiUserData: ApiUser,
        apiDealersData: ApiDealerInformation[],
        currentDealers: ReadonlyArray<DealerAssignmentRow>,
    ): void {
        // Load user data into userInfo signal
        this.userInfo.set(apiUserData);

        // Extract client type name and map to internal role for layout
        const roleName =
            apiUserData.userClientAssignments?.[0]?.clientTypeName ||
            (apiUserData.metadata?.['roleName'] as string) ||
            'User';

        this.clientType.set(this.formatRoleName(roleName));
        this.userRole.set(this.mapRoleToInternalRole(roleName));

        // Load the dealers data into organizationDealers signal
        this.organizationDealers.set(
            apiDealersData.map((add) => ({
                dealerName: add.clientInformation.businessName,
                dealerId: add.clientInformation.alias ?? '',
                label: add.clientInformation.businessName,
                value: add.clientInformation.alias ?? '',
            })),
        );

        // Map current dealers into the selectedDealers two-binding signal
        const mappedCurrentDealers = currentDealers.map((add) => ({
            ...add,
            dealerName: add.businessName ?? add.dealerName ?? '',
            dealerId: add.alias ?? add.dealerId ?? '',
            label: add.businessName ?? add.dealerName ?? add.label ?? '',
            value: add.alias ?? add.dealerId ?? add.value ?? '',
        }));

        this.selectedDealers.set(mappedCurrentDealers);
        this.originalSelectedDealers.set(mappedCurrentDealers);
        // Patch user form
        this.patchUserForm();
        this.createdByDisplayName.set(getCreatedByDisplayLabel(apiUserData));
        this.resolveCreatedByDisplayName(apiUserData);

        const uid = (apiUserData.userId ?? '').trim();
        if (uid) {
            this.loadLicenses(uid);
        } else {
            this.licenseRows.set([]);
        }
    }

    /**
     * @private
     * @description Fetches creator via GET `/api/users/{id}` when payload carries a creator UUID (reload-safe)
     * @param user - User entity from GET `/api/users/{id}` or hydrated navigation state
     * @returns void
     */
    private resolveCreatedByDisplayName(user: ApiUser): void {
        const creatorId = extractCreatedByUserId(user);
        if (!creatorId) {
            return;
        }

        this.userService
            .getUserById(creatorId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((): Observable<null> => of(null)),
            )
            .subscribe((response) => {
                const current = this.userInfo();
                if (!current || extractCreatedByUserId(current) !== creatorId) {
                    return;
                }

                if (response === null || !response.data) {
                    this.createdByDisplayName.set('N/A');
                    return;
                }

                const label = formatCreatedByResolvedName(
                    response.data.firstName,
                    response.data.lastName,
                );
                this.createdByDisplayName.set(label);
            });
    }

    /**
     * @private
     * @description Builds a partial DTO containing only keys whose values differ from the original snapshot
     * @param original - Last known persisted user fields
     * @param current - Current form raw values
     * @returns Patch object safe to send to the update endpoint
     */
    private buildPatch(original: UpdateUserDto, current: UpdateUserDto): Partial<UpdateUserDto> {
        const patch: Partial<UpdateUserDto> = {};

        (Object.keys(current) as (keyof UpdateUserDto)[]).forEach((k) => {
            const changed = current[k] !== original[k];
            const intentionallyCleaned = original[k] != null && current[k] == null;

            if ((changed || intentionallyCleaned) && current[k] !== '') {
                patch[k] = current[k] as never;
            }
        });
        return patch;
    }

    /**
     * @private
     * @description Detects whether password fields contain text for conditional validation
     * @returns Flags describing password field presence
     */
    private checkPasswordFields(): { hasPassword: boolean; hasConfirmPassword: boolean } {
        const form = this.form() as FormGroup;
        const password = form.get('password');
        const confirmPassword = form.get('passwordConfirmation');

        if (!password || !confirmPassword) {
            return { hasPassword: false, hasConfirmPassword: false };
        }

        return {
            hasPassword: password.value !== '',
            hasConfirmPassword: confirmPassword.value !== '',
        };
    }

    /**
     * @private
     * @description Applies required validators when only one side of the password pair is filled
     * @returns void
     */
    private makeOtherPasswordFieldRequired(): void {
        const form = this.form() as FormGroup;
        const password = form.get('password');
        const confirmPassword = form.get('passwordConfirmation');

        if (!password || !confirmPassword) return;

        const hasPassword = password.value !== '';
        const hasConfirmPassword = confirmPassword.value !== '';

        if (hasPassword && !hasConfirmPassword) {
            confirmPassword.setValidators([Validators.required]);
            confirmPassword.markAsTouched();
        } else if (hasConfirmPassword && !hasPassword) {
            password.setValidators([Validators.required]);
            password.markAsTouched();
        }
    }

    /**
     * @private
     * @description Maps API client type labels to internal layout role keys
     * @param role - Human-readable or squashed role label
     * @returns Internal `UserRoles` key (excluding `none`)
     */
    private mapRoleToInternalRole(role: string): Exclude<UserRoles, 'none'> {
        const lowerRole = role?.toLowerCase() || '';
        if (lowerRole.includes('super')) return 'superAdmin';
        if (lowerRole.includes('dealer')) return 'dealerAdmin';
        if (lowerRole.includes('advertiser')) return 'advertiser';
        return 'dealerAdmin'; // Default to dealerAdmin layout
    }

    /**
     * @private
     * @description Normalizes role strings for badge display (spacing and title case)
     * @param role - Raw role label from API or navigation state
     * @returns Friendly label for the UI badge
     */
    private formatRoleName(role: string): string {
        if (!role || role === '--') return 'User';

        // Normalize to Title Case with spaces for known squashed patterns
        const trimmedRole = role.trim();
        const lowerRole = trimmedRole.toLowerCase();

        // Handle explicit squashed cases (case-insensitive)
        if (lowerRole === 'superadmin') return 'Super Admin';
        if (lowerRole === 'dealeradmin') return 'Dealer Admin';

        // If it already contains a space, just return it
        if (trimmedRole.includes(' ')) return trimmedRole;

        // Otherwise, split camelCase and capitalize
        return trimmedRole
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
            .replace(/^./, (s) => s.toUpperCase());
    }

    /**
     * @private
     * @description Marks every non-password control touched and reports aggregate validity
     * @returns True when all inspected controls are valid
     */
    private validateForm(): boolean {
        const form = this.form() as FormGroup;
        const allKeys = Object.keys(form.controls).filter(
            (key) => key !== 'password' && key !== 'passwordConfirmation',
        );

        let isAllValid = true;
        allKeys.forEach((key) => {
            const control = form.get(key);
            control?.markAsTouched();
            if (!control?.valid) {
                isAllValid = false;
            }
        });

        return isAllValid;
    }

    /** ============================== API Calls ========================================== */

    /**
     * @private
     * @description Loads the user row and, for eligible roles, prefetches related dealer collections
     * @param userId - Route user identifier
     * @param _filter - Reserved dealer filter (same shape as the route effect); dealer queries use `userDealersFilter`
     * @returns void
     */
    private loadUser(userId: string, _filter: Record<string, FilterOperator>): void {
        const requests: Partial<AggregatedSingleUserRequests> = {
            user: this.userService.getUserById(userId),
        };

        if (this.allowedRoles.includes(this.userRole())) {
            requests['dealers'] = this.dealerService.getAll({
                filters: this.userDealersFilter() as Record<string, FilterOperator>,
            });
            requests['currentDealers'] = this.dealerService.getByUserId(userId);
        }

        forkJoin(requests)
            .pipe(
                withLoading(this.isLoadingUser),
                catchError(() => EMPTY),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((res) => {
                const payload = res as LoadSingleUserResponse;
                const userPayload = payload.user?.data;
                if (!userPayload) {
                    return;
                }

                this.mapDataToUI(
                    userPayload,
                    payload.dealers?.data ?? [],
                    payload.currentDealers ?? [],
                );
            });
    }

    /**
     * @private
     * @description Loads license rows owned by the given user and maps them to the licenses table UI model.
     * @param userId - Owner user id used in the licenses API filter
     * @returns void
     */
    private loadLicenses(userId: string): void {
        this.licensesLoading.set(true);
        this.licenseRows.set([]);
        this.licensesService
            .getAll({
                filters: { ownerId: userId },
                omitDefaultLimit: true,
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.licensesLoading.set(false)),
            )
            .subscribe({
                next: (res) => {
                    const mapped = (res.data ?? []).map((l) => this.licensesService.mapToUI(l));
                    this.licenseRows.set(mapped);
                },
                error: (err: unknown) => {
                    Logger.error('SingleUserComponent', 'Failed to load licenses', err);
                    this.licenseRows.set([]);
                },
            });
    }

    /**
     * @private
     * @description Refreshes the dealer directory when search returns empty
     * @returns void
     */
    private loadUserDealers(): void {
        this.dealerService
            .getAll({ filters: this.userDealersFilter() as Record<string, FilterOperator> })
            .pipe(withLoading(this.isSearchingDealer), takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
                this.organizationDealers.set(
                    res.data.map(
                        (add) =>
                            ({
                                dealerName: add.clientInformation.businessName,
                                dealerId: add.clientInformation.alias ?? '',
                                label: add.clientInformation.businessName,
                                value: add.clientInformation.alias ?? '',
                            }) as UiDealersAutocomplete,
                    ),
                );
            });
    }

    /**
     * @private
     * @description PATCHes the user with changed fields, then exits edit mode and shows confirmation
     * @param userId - Target user id
     * @param userUpdatedData - Partial update payload
     * @returns void
     */
    private updateUser(userId: string, userUpdatedData: Partial<UpdateUserDto>): void {
        this.userService
            .update(userId, userUpdatedData as UpdateUserDto)
            .pipe(withLoading(this.isUpdatingUser), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.isUserUpdated.set(true);
                this.isEditingUser.set(false);
            });
    }
}
